<script setup lang="ts">
import Product from "~/models/Product";
import { formatPrice } from "@/utils/pricing";
import mastercardLogo from "@/assets/images/brands/mastercard_logo.svg?url";
import visaLogo from "@/assets/images/brands/visa_logo.svg";
import oxxoLogo from "@/assets/images/brands/oxxo_logo.svg";
import paypalLogo from "@/assets/images/brands/paypal_logo.svg";
import SafeHtml from "@/components/utils/SafeHtml.vue";
import DiscountIcon from "@/components/icons/DiscountIcon.vue";
import ContentItem, { type IContentItem } from "~/models/ContentItem";

const props = defineProps({
  product: {
    type: Product,
    required: true,
  },
  showUsedPrice: Boolean,
});
const couponWalletAmount = computed(() =>
  !props.product.coupon?.with_wallet
    ? 0
    : props.product.coupon.discount_percentage > 0
      ? (props.product.getFinalCurrentPrice() *
          props.product.coupon.discount_percentage) /
        100
      : props.product.coupon.discount_amount_with_iva,
);
const itemClasses = computed(
  () =>
    "d-flex flex-wrap align-items-center gap-2 text-secondary fw-light py-2 px-2 small-sm small-md border border-light-subtle border-md-0",
);
const loading = ref(false);
const error = ref<string>();
const contentItems = reactive<ContentItem[]>([]);
const getContentItems = async () => {
  const url = `/shop-v1/content-sections/product-detail/blocks/product-includes/rev/current.json`;
  const { status, data, error } = await useApiFetch(url);
  if (status.value !== "success") {
    throw error.value;
  }
  const objects = JSON.parse(
    (data.value as { content: string }).content,
  ) as IContentItem[];
  return ContentItem.fromArray(objects);
};
const loadContentItems = async () => {
  loading.value = true;
  error.value = undefined;
  try {
    contentItems.splice(0);
    contentItems.push(...(await getContentItems()));
  } catch (err) {
    error.value = await parseError(err);
  } finally {
    loading.value = false;
  }
};

const getItem = (key: string): ContentItem | null => {
  return contentItems.find(
    (item) => item.item_key === key,
  ) as ContentItem | null;
};

const pickupItem = computed(() => getItem("pickup"));
const walletItem = computed(() => getItem("wallet"));
const shippingItem = computed(() => getItem("shipping"));
const lastPiecesItem = computed(() => getItem("last_pieces"));
const offerItem = computed(() => getItem("offer"));
const couponItem = computed(() => getItem("coupon"));

await loadContentItems();
</script>

<template>
  <aside class="pb-2 d-flex flex-column justify-content-between h-100">
    <div>
      <div class="fw-bold mb-3 small fw-bold">Este producto incluye</div>
      <p v-if="loading" class="placeholder-glow">
        <span class="placeholder col-12 placeholder-lg bg-light"></span>
        <span class="placeholder col-12 placeholder-lg bg-light"></span>
        <span class="placeholder col-12 placeholder-lg bg-light"></span>
        <span class="placeholder col-12 placeholder-lg bg-light"></span>
        <span class="placeholder col-12 placeholder-lg bg-light"></span>
      </p>
      <ul v-else class="product-promotion-list mx-0 px-0 mb-4 d-grid gap-2">
        <li
          v-if="
            walletItem &&
            (product.wallet_promotion_amount > 0 ||
              product.mwi_wallet_amount_with_tax > 0)
          "
          :class="[itemClasses]"
        >
          <i class="bi bi-credit-card fs-5 text-success lh-1"></i>
          <span class="fw-bold">
            <safe-html
              tag="span"
              :html="
                formatPrice(
                  product.wallet_promotion_amount ||
                    product.mwi_wallet_amount_with_tax,
                )
              "
            />
            <span class="text-info ms-1">{{ walletItem.label }}</span>
            <div>
              <small
                v-if="product.mwi_wallet_amount_with_tax > 0"
                class="badge text-bg-light"
                >a 1 pago</small
              >
            </div>
          </span>
          <div v-if="walletItem.value" class="prom-item-value">
            <safe-html tag="span" :html="walletItem.value" />
          </div>
        </li>
        <li
          v-if="shippingItem && product.has_shipping_promotion"
          :class="[itemClasses]"
        >
          <i class="bi bi-truck fs-5 lh-1"></i>
          <span>{{ shippingItem.label }}</span>
          <div v-if="shippingItem.value" class="prom-item-value">
            <safe-html tag="span" :html="shippingItem.value" />
          </div>
        </li>
        <li
          v-if="pickupItem && product.stock_stores > 0"
          :class="[itemClasses]"
        >
          <i class="bi bi-clock-fill text-danger fs-6 align-middle" />
          <utils-safe-html
            tag="span"
            :html="pickupItem.label || ''"
          ></utils-safe-html>
          <div v-if="pickupItem.value" class="prom-item-value">
            <safe-html tag="span" :html="pickupItem.value" />
          </div>
        </li>
        <li
          v-if="lastPiecesItem && product.has_last_stock"
          :class="[itemClasses]"
        >
          <i class="bi bi-fire text-fire fs-5 lh-1"></i>
          <span>{{ lastPiecesItem.label }}</span>
          <div v-if="lastPiecesItem.value" class="prom-item-value">
            <safe-html tag="span" :html="lastPiecesItem.value" />
          </div>
        </li>
        <li v-if="offerItem && product.hasDiscount()" :class="[itemClasses]">
          <i class="bi bi-tag fs-5 text-danger lh-1"></i>
          <span
            >-{{ product.getPercentDiscountText() }} {{ offerItem.label }}</span
          >
          <div v-if="offerItem.value" class="prom-item-value">
            <safe-html tag="span" :html="offerItem.value" />
          </div>
        </li>
        <li v-if="couponItem && product.coupon" :class="[itemClasses]">
          <template v-if="couponWalletAmount > 0">
            <discount-icon class="fs-5 lh-1" />
            <span>
              <safe-html
                class="text-success fw-bold"
                tag="span"
                :html="formatPrice(couponWalletAmount)"
              />
              <span class="position-relative">
                <span class="ms-1">Cupón</span>
                <span
                  class="position-absolute top-100 translate-middle start-50 mt-1 badge text-bg-light rounded-pill p-0"
                  style="margin: 0.1rem"
                  >en monedero</span
                >
              </span>
            </span>
            <span class="text-info fw-bold">
              {{ product.coupon.code }}
            </span>
          </template>
          <template v-else>
            <discount-icon class="fs-5 lh-1" />
            <span>{{ couponItem.label }}</span>
            <span class="fw-bold text-info">{{ product.coupon.code }}</span>
          </template>
          <div v-if="couponItem.value" class="prom-item-value">
            <safe-html tag="span" :html="couponItem.value" />
          </div>
        </li>
        <!-- OCULTO HASTA QUE FUNCIONE -->
        <!-- <li class="active" :class="[itemClasses]">
          <i class="bi bi-shield-fill fs-5"></i>
          <span>Agrega plan de protección</span>
        </li> -->
      </ul>
    </div>
    <div v-if="false && product.monthly_offer_stores_info.length > 0">
      <h6 class="fw-bold mb-3 small">Disponible sólo en</h6>
      <div
        id="monthStoresAccordion"
        class="accordion mb-4"
        style="
          --bs-accordion-btn-bg: transparent;
          --bs-accordion-active-bg: transparent;
        "
      >
        <div
          v-for="(storeItem, index) in product.monthly_offer_stores_info"
          :key="index"
          class="accordion-item"
        >
          <h2 class="accordion-header">
            <button
              class="accordion-button d-flex align-items-center gap-2 text-info fw-semibold py-2 px-2 small-sm small-md"
              type="button"
              data-bs-toggle="collapse"
              :data-bs-target="'#monthStoresCollapse' + index"
              aria-expanded="true"
              :aria-controls="'monthStoresCollapse' + index"
            >
              <i class="bi bi-check fs-5 lh-1"></i>
              <safe-html tag="span" :html="offerItem.title" />
            </button>
          </h2>
          <div
            :id="'monthStoresCollapse' + index"
            class="accordion-collapse collapse show"
          >
            <div class="accordion-body p-0">
              <div class="list-group list-group-flush">
                <button
                  v-for="(store, subindex) in offerItem.stores"
                  :key="index + '' + subindex"
                  type="button"
                  class="list-group-item list-group-item-action py-1"
                  href="#"
                  @click.prevent
                >
                  <small>{{ store }}</small>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div>
      <div class="fw-bold mb-3 small">Métodos de pago</div>
      <ul class="product-payment-list d-flex gap-2 mb-2">
        <li>
          <img :src="mastercardLogo" width="40" height="30" alt="Mastercard" />
        </li>
        <li>
          <img :src="visaLogo" width="40" height="30" alt="Visa" />
        </li>
        <li>
          <img :src="paypalLogo" width="40" height="30" alt="PayPal" />
        </li>
        <li>
          <img :src="oxxoLogo" width="40" height="30" alt="Oxxo" />
        </li>
      </ul>
      <div class="text-end">
        <router-link
          :to="{ name: 'support.payment_ways' }"
          class="small-sm text-decoration-none"
        >
          Ver todas las <span class="text-info">formas</span> de pago
        </router-link>
      </div>
    </div>
  </aside>
</template>

<style lang="scss" scoped>
@use "@/assets/scss/bootstrap-helpers" as *;

.product-promotion-list {
  list-style-type: none;
  // @include media-breakpoint-up(md) {
  //   max-width: 220px;
  // }
  grid-template-columns: 1fr 1fr;
  @include media-breakpoint-up(md) {
    grid-template-columns: 1fr;
  }
  li {
    position: relative;
    cursor: pointer;
    text-align: center;
    > span {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      // Comentado porque queremos que se vea todo el texto
      // line-clamp: 1;
      // -webkit-line-clamp: 1;
      // overflow: hidden;
      // text-overflow: ellipsis;
      &:hover {
        display: inline;
      }
    }
    > .prom-item-value {
      display: none;
    }
    * {
      z-index: 1;
    }
    &::before {
      content: " ";
      display: block;
      position: absolute;
      width: 0%;
      height: 100%;
      background-color: var(--bs-light);
      z-index: 0;
      top: 0;
      left: 0;
      opacity: 0;
    }
    &::after {
      content: "\F4FA";
      font-family: "bootstrap-icons";
      z-index: 1;
      margin-left: auto;
    }
    &:hover,
    &.active {
      &::before {
        transition: 0.25s ease-in;
        width: 100%;
        opacity: 1;
      }
      i {
        color: var(--bs-secondary);
      }
      &::after {
        content: "\F4F9";
      }
      > span {
        font-weight: bold;
      }
      > .prom-item-value {
        display: block;
      }
    }
  }
}
.product-payment-list {
  list-style-type: none;
  padding: 0;
  margin: 0;
  li {
    border: 1px solid var(--bs-light);
    padding: 4px;
    text-align: center;
    flex: 1 1 0;
    img {
      width: auto;
      width: 100%;
      height: 100%;
      max-width: 40px;
      max-height: 40px;
    }
  }
}
</style>
